import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Pencil, Plus, X } from "lucide-react";
import { api, gbp, formatApiErrorDetail } from "../../lib/api";

const inputCls = "w-full bg-transparent border border-[#27272A] px-3 py-2.5 text-sm outline-none focus:border-[#FFCC00] placeholder:text-white/30";
const EMPTY = { title: "", category_slug: "electronics", condition: "Grade A — Boxed New", member_price: "", rrp_total: "", stock_total: "", stock_remaining: "", units: "", images_text: "", description: "", manifest_text: "", status: "coming_soon", is_hero: false, ends_hours: "24" };

const parseManifest = (text) =>
  text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const [item, qty, rrp] = l.split("|").map((s) => s.trim());
    return { item: item || "Item", qty: parseInt(qty) || 1, rrp_each: parseFloat(rrp) || 0 };
  });

export const AdminDeals = () => {
  const [deals, setDeals] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | deal object
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/deals").then(({ data }) => setDeals(data));
    api.get("/categories").then(({ data }) => setCats(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (d) => {
    setForm({
      title: d.title, category_slug: d.category_slug, condition: d.condition,
      member_price: d.member_price, rrp_total: d.rrp_total, stock_total: d.stock_total,
      stock_remaining: d.stock_remaining, units: d.units, images_text: (d.images || []).join("\n"),
      description: d.description, status: d.status, is_hero: d.is_hero, ends_hours: "",
      manifest_text: (d.manifest || []).map((m) => `${m.item} | ${m.qty} | ${m.rrp_each}`).join("\n"),
    });
    setEditing(d);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      title: form.title, category_slug: form.category_slug, condition: form.condition,
      member_price: parseFloat(form.member_price), rrp_total: parseFloat(form.rrp_total),
      stock_total: parseInt(form.stock_total), units: parseInt(form.units),
      images: form.images_text.split("\n").map((s) => s.trim()).filter(Boolean),
      description: form.description,
      manifest: parseManifest(form.manifest_text), status: form.status, is_hero: form.is_hero,
    };
    try {
      if (editing === "new") {
        payload.ends_hours = parseFloat(form.ends_hours) || 24;
        await api.post("/admin/deals", payload);
        toast.success("Deal created");
      } else {
        if (form.ends_hours) payload.ends_hours = parseFloat(form.ends_hours);
        if (form.stock_remaining !== "") payload.stock_remaining = parseInt(form.stock_remaining);
        const { data } = await api.put(`/admin/deals/${editing.id}`, payload);
        toast.success(data.notified > 0 ? `Deal updated — LIVE. ${data.notified} watcher(s) emailed.` : "Deal updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-2xl">ALL DEALS ({deals.length})</h2>
        <button data-testid="admin-new-deal-button" onClick={openNew} className="btn-industrial px-5 py-2.5 text-lg flex items-center gap-2"><Plus size={16} /> NEW DEAL</button>
      </div>

      {editing && (
        <form onSubmit={save} className="border-2 border-[#FFCC00] p-6 sm:p-8 mb-10 bg-[#111111]" data-testid="admin-deal-form">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-display text-2xl">{editing === "new" ? "CREATE DEAL" : `EDIT — ${editing.sku}`}</h3>
            <button type="button" data-testid="admin-form-close-button" onClick={() => setEditing(null)} className="text-white/40 hover:text-[#FF3B30]"><X size={20} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="admin-form-title" required placeholder="TITLE" value={form.title} onChange={set("title")} className={`${inputCls} sm:col-span-2`} />
            <select data-testid="admin-form-category" value={form.category_slug} onChange={set("category_slug")} className={`${inputCls} bg-[#111111]`}>
              {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
            <input data-testid="admin-form-condition" required placeholder="CONDITION GRADE" value={form.condition} onChange={set("condition")} className={inputCls} />
            <input data-testid="admin-form-price" required type="number" step="0.01" min="0" placeholder="MEMBER PRICE £" value={form.member_price} onChange={set("member_price")} className={inputCls} />
            <input data-testid="admin-form-rrp" required type="number" step="0.01" min="0" placeholder="RRP TOTAL £" value={form.rrp_total} onChange={set("rrp_total")} className={inputCls} />
            <input data-testid="admin-form-stock-total" required type="number" min="0" placeholder="STOCK (LOTS)" value={form.stock_total} onChange={set("stock_total")} className={inputCls} />
            {editing !== "new" && (
              <input data-testid="admin-form-stock-remaining" type="number" min="0" placeholder="STOCK REMAINING" value={form.stock_remaining} onChange={set("stock_remaining")} className={inputCls} />
            )}
            <input data-testid="admin-form-units" required type="number" min="1" placeholder="UNITS PER LOT TOTAL" value={form.units} onChange={set("units")} className={inputCls} />
            <input data-testid="admin-form-ends" type="number" step="0.5" min="0" placeholder={editing === "new" ? "ENDS IN HOURS (24)" : "RESET TIMER — HOURS (BLANK = KEEP)"} value={form.ends_hours} onChange={set("ends_hours")} className={inputCls} />
            <textarea data-testid="admin-form-images" required rows={3} placeholder={"IMAGE URLS — ONE PER LINE (FIRST = MAIN PHOTO)"} value={form.images_text} onChange={set("images_text")} className={`${inputCls} sm:col-span-2`} />
            <textarea data-testid="admin-form-description" required rows={3} placeholder="DESCRIPTION" value={form.description} onChange={set("description")} className={`${inputCls} sm:col-span-2`} />
            <textarea data-testid="admin-form-manifest" rows={4} placeholder={"MANIFEST — ONE LINE PER ITEM:\nBluetooth speakers | 34 | 49.99"} value={form.manifest_text} onChange={set("manifest_text")} className={`${inputCls} sm:col-span-2`} />
            <select data-testid="admin-form-status" value={form.status} onChange={set("status")} className={`${inputCls} bg-[#111111]`}>
              <option value="coming_soon">COMING SOON</option>
              <option value="live">LIVE</option>
              <option value="ended">ENDED</option>
            </select>
            <label className="flex items-center gap-3 text-sm text-white/70 border border-[#27272A] px-3">
              <input data-testid="admin-form-hero" type="checkbox" checked={form.is_hero} onChange={set("is_hero")} className="accent-[#FFCC00]" />
              HERO DEAL OF THE DAY
            </label>
          </div>
          <button data-testid="admin-form-save-button" disabled={busy} className="btn-industrial px-8 py-3 text-xl mt-6">
            {busy ? "SAVING…" : "SAVE DEAL"}
          </button>
          {editing !== "new" && editing.status === "coming_soon" && form.status === "live" && (
            <span className="ml-4 text-xs text-[#FFCC00]">Setting LIVE will email {editing.watchers} watcher(s).</span>
          )}
        </form>
      )}

      <div className="border border-[#27272A] overflow-x-auto">
        <table className="w-full text-xs sm:text-sm min-w-[760px]" data-testid="admin-deals-table">
          <thead>
            <tr className="text-left text-white/40 uppercase text-[10px] tracking-widest border-b border-[#27272A]">
              <th className="px-4 py-3">SKU</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Price</th><th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-right">Watchers</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {deals.map((d) => (
              <tr key={d.id} className="border-b border-[#27272A] last:border-0 hover:bg-[#111111]">
                <td className="px-4 py-3 text-white/50">{d.sku}{d.is_hero && <span className="ml-2 text-[#FF3B30]">★</span>}</td>
                <td className="px-4 py-3">{d.title}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${d.status === "live" ? "border-[#FFCC00] text-[#FFCC00]" : d.status === "coming_soon" ? "border-white/30 text-white/50" : "border-[#FF3B30] text-[#FF3B30]"}`}>
                    {d.status.replace("_", " ")}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-[#FFCC00]">{gbp(d.member_price)}</td>
                <td className="px-4 py-3 text-right">{d.stock_remaining}/{d.stock_total}</td>
                <td className="px-4 py-3 text-right">{d.watchers}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`admin-edit-deal-${d.slug}`} onClick={() => openEdit(d)} className="text-white/50 hover:text-[#FFCC00] p-1"><Pencil size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
